// src/services/enhancers/ConfigServiceEnhancer.js - FIXED VERSION
// Fixed import paths to match your project structure

import { ErrorTrackingService } from '../ErrorTrackingService';

export class ConfigServiceEnhancer {
  
  // Enhanced configuration initialization with error tracking
  static async initializeConfig() {
    ErrorTrackingService.addBreadcrumb(
      'Configuration initialization started',
      'config',
      'info'
    );

    try {
      // FIXED: Correct import path for ConfigService in src/config/
      const { ConfigService } = await import('../ConfigService');
      const result = ConfigService.initialize();
      
      if (result) {
        ErrorTrackingService.addBreadcrumb(
          'Configuration initialized successfully',
          'config',
          'info',
          { 
            source: ConfigService.isEnvironmentConfigured ? 
              (ConfigService.isEnvironmentConfigured() ? 'environment' : 'fallback') : 'unknown',
            environment: ConfigService.isDevelopment ? 
              (ConfigService.isDevelopment() ? 'development' : 'production') : 'unknown'
          }
        );
      } else {
        ErrorTrackingService.captureError(
          new Error('Configuration initialization failed'),
          {
            service: 'ConfigService',
            method: 'initialize',
            level: 'warning',
          }
        );
      }
      
      return result;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'initialize', {
        component: 'ConfigServiceEnhancer',
      });
      
      // Don't throw - let app continue with fallback
      console.warn('⚠️ ConfigService initialization failed, continuing with fallback');
      return false;
    }
  }

  // Enhanced Firebase configuration retrieval with monitoring
  static getFirebaseConfig() {
    ErrorTrackingService.addBreadcrumb(
      'Firebase configuration requested',
      'config',
      'info'
    );

    try {
      // FIXED: Correct import path
      const { ConfigService } = require('../ConfigService');
      const firebaseConfig = ConfigService.getFirebaseConfig();
      
      if (firebaseConfig) {
        // Track configuration source for analytics
        const isEnvironmentConfig = ConfigService.isEnvironmentConfigured ? 
          ConfigService.isEnvironmentConfigured() : false;
        
        ErrorTrackingService.addBreadcrumb(
          'Firebase configuration retrieved',
          'config',
          'info',
          { 
            source: isEnvironmentConfig ? 'environment_variables' : 'hardcoded_fallback',
            hasApiKey: !!firebaseConfig.apiKey,
            hasProjectId: !!firebaseConfig.projectId
          }
        );

        // Track fallback usage for production monitoring
        if (!isEnvironmentConfig && ConfigService.isProduction && ConfigService.isProduction()) {
          ErrorTrackingService.captureError(
            new Error('Production environment using fallback Firebase configuration'),
            {
              service: 'ConfigService',
              method: 'getFirebaseConfig',
              level: 'warning',
              environment: 'production',
              configSource: 'fallback'
            }
          );
        }
      } else {
        ErrorTrackingService.captureError(
          new Error('Firebase configuration is null'),
          {
            service: 'ConfigService',
            method: 'getFirebaseConfig',
            level: 'error',
          }
        );
      }
      
      return firebaseConfig;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'getFirebaseConfig', {
        component: 'ConfigServiceEnhancer',
      });
      
      // Return hardcoded fallback to prevent app breakage
      console.warn('⚠️ ConfigService error, using hardcoded Firebase config');
      return {
        apiKey: "AIzaSyCLtCvPH6esCTg4aSOuBt1PKzgLTtFaABM",
        authDomain: "awesome-habitat-386117.firebaseapp.com",
        projectId: "awesome-habitat-386117",
        storageBucket: "awesome-habitat-386117.firebasestorage.app",
        messagingSenderId: "652532757120",
        appId: "1:652532757120:web:3582500396a740f020ac65",
        measurementId: "G-KSNCQ5GRV3"
      };
    }
  }

  // Enhanced API configuration with your existing structure
  static getEnhancedApiConfig() {
    ErrorTrackingService.addBreadcrumb(
      'Enhanced API configuration requested',
      'config',
      'info'
    );

    try {
      // FIXED: Correct import path
      const { ConfigService } = require('../ConfigService');
      const enhancedConfig = ConfigService.getEnhancedApiConfig();
      
      ErrorTrackingService.addBreadcrumb(
        'Enhanced API configuration retrieved',
        'config',
        'info',
        { 
          hasFirebaseConfig: !!enhancedConfig.firebase,
          hasEndpoints: !!enhancedConfig.endpoints,
          hasServices: !!enhancedConfig.services,
          configSource: enhancedConfig._meta?.source || 'unknown'
        }
      );
      
      return enhancedConfig;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'getEnhancedApiConfig', {
        component: 'ConfigServiceEnhancer',
      });
      
      // Fallback to original apiConfig (non-breaking)
      try {
        // FIXED: Correct import path for apiConfig
        const { apiConfig } = require('../../config/apiConfig');
        
        ErrorTrackingService.addBreadcrumb(
          'Fallback to original apiConfig',
          'config',
          'warning',
          { reason: 'enhanced_config_failed' }
        );
        
        return apiConfig;
      } catch (fallbackError) {
        ErrorTrackingService.captureError(fallbackError, {
          service: 'ConfigService',
          method: 'getEnhancedApiConfig',
          level: 'error',
          fallbackAttempt: true
        });
        
        // Ultimate fallback
        return {
          firebase: this.getFirebaseConfig(),
          endpoints: {},
          services: {}
        };
      }
    }
  }

  // Enhanced configuration validation with detailed tracking
  static validateConfiguration() {
    ErrorTrackingService.addBreadcrumb(
      'Configuration validation started',
      'config',
      'info'
    );

    try {
      // FIXED: Correct import path
      const { ConfigService } = require('../ConfigService');
      const validation = ConfigService.validateConfiguration();
      
      if (validation.isValid) {
        ErrorTrackingService.addBreadcrumb(
          'Configuration validation passed',
          'config',
          'info',
          { 
            warningCount: validation.warnings?.length || 0,
            environment: ConfigService.isDevelopment ? 
              (ConfigService.isDevelopment() ? 'development' : 'production') : 'unknown'
          }
        );
      } else {
        ErrorTrackingService.captureError(
          new Error(`Configuration validation failed: ${validation.errors.join(', ')}`),
          {
            service: 'ConfigService',
            method: 'validateConfiguration',
            validationErrors: validation.errors,
            validationWarnings: validation.warnings,
            level: 'warning',
          }
        );
      }
      
      // Track warnings separately for production monitoring
      if (validation.warnings?.length > 0) {
        ErrorTrackingService.addBreadcrumb(
          'Configuration validation warnings',
          'config',
          'warning',
          { warnings: validation.warnings }
        );
      }
      
      return validation;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'validateConfiguration', {
        component: 'ConfigServiceEnhancer',
      });
      
      // Return safe fallback
      return {
        isValid: false,
        errors: ['Configuration validation failed'],
        warnings: [],
        timestamp: new Date().toISOString()
      };
    }
  }

  // Enhanced configuration status monitoring
  static getConfigurationStatus() {
    try {
      // FIXED: Correct import path
      const { ConfigService } = require('../ConfigService');
      const status = ConfigService.getStatus();
      
      ErrorTrackingService.addBreadcrumb(
        'Configuration status requested',
        'config',
        'info',
        { 
          initialized: status.initialized,
          environment: status.environment,
          configSource: status.firebaseConfigSource
        }
      );
      
      return status;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'getStatus', {
        component: 'ConfigServiceEnhancer',
      });
      
      return {
        initialized: false,
        environment: 'unknown',
        firebaseConfigSource: 'error',
        validation: { isValid: false, errors: ['Status check failed'] },
        timestamp: new Date().toISOString()
      };
    }
  }

  // Enhanced configuration refresh for development
  static refreshConfiguration() {
    ErrorTrackingService.addBreadcrumb(
      'Configuration refresh started',
      'config',
      'info'
    );

    try {
      // FIXED: Correct import path
      const { ConfigService } = require('../ConfigService');
      const result = ConfigService.refresh();
      
      if (result) {
        ErrorTrackingService.addBreadcrumb(
          'Configuration refresh successful',
          'config',
          'info'
        );
      } else {
        ErrorTrackingService.captureError(
          new Error('Configuration refresh failed'),
          {
            service: 'ConfigService',
            method: 'refresh',
            level: 'warning',
          }
        );
      }
      
      return result;
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'refresh', {
        component: 'ConfigServiceEnhancer',
      });
      
      return false;
    }
  }

  // Integration helper for your existing ConfigProvider
  static getConfigForProvider() {
    try {
      // Get enhanced configuration that works with your existing structure
      const enhancedConfig = this.getEnhancedApiConfig();
      
      // Return in the format your ConfigProvider expects
      return {
        api: enhancedConfig,
        _enhanced: true,
        _source: enhancedConfig._meta?.source || 'unknown',
        _timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      ErrorTrackingService.wrapServiceError(error, 'ConfigService', 'getConfigForProvider', {
        component: 'ConfigServiceEnhancer',
      });
      
      // Fallback to original structure (non-breaking)
      try {
        // FIXED: Correct import path
        const { apiConfig } = require('../../config/apiConfig');
        return {
          api: apiConfig,
          _enhanced: false,
          _source: 'fallback',
          _timestamp: new Date().toISOString()
        };
      } catch (fallbackError) {
        // Ultimate fallback
        return {
          api: {
            firebase: this.getFirebaseConfig(),
            endpoints: {},
            services: {}
          },
          _enhanced: false,
          _source: 'error_fallback',
          _timestamp: new Date().toISOString()
        };
      }
    }
  }
}